'use client';

import Link from 'next/link';
import { useState } from 'react';
import { ArrowRight, MapPin, Star } from 'lucide-react';
import { CustomerAccountEntry } from '@/components/customer-surface/CustomerAccountEntry';
import { ImageWithFallback } from '@/components/ui/ImageWithFallback';
import {
  GuestV2Button,
  GuestV2Container,
  GuestV2Page,
  GuestV2Section,
  GuestV2StatusPill,
  GuestV2TopBar,
} from '@/components/guest-v2/GuestV2';
import { cn } from '@/components/ui/utils';
import type { BusinessLocationsMarker } from '@/components/maps/BusinessLocationsMap';
import { placePath, placeSubPath } from '@/lib/routing/place-href';
import type { PlaceMenuViewMode } from '@/lib/customer/place-menu-view';
import type { PlaceBranch, PlaceBusiness } from '@/lib/public/resolve-place';
import PlaceHubV2Map from './PlaceHubV2Map';

/** The v2 business QR destination: choose the right location with a single scan. */
export default function PlaceHubV2({
  business,
  branches,
  menuViewsByBranch = {},
}: {
  business: PlaceBusiness;
  branches: readonly PlaceBranch[];
  menuViewsByBranch?: Readonly<Record<string, PlaceMenuViewMode | undefined>>;
}) {
  const ordered = [...branches].sort((a, b) => Number(b.openState === 'open') - Number(a.openState === 'open'));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileMapOpen, setMobileMapOpen] = useState(false);
  const mapMarkers: (BusinessLocationsMarker & { menuHref: string; detailsHref: string })[] = ordered.flatMap((branch, index) =>
    branch.geoLat !== null && branch.geoLng !== null &&
    Number.isFinite(branch.geoLat) && Number.isFinite(branch.geoLng) &&
    Math.abs(branch.geoLat) <= 90 && Math.abs(branch.geoLng) <= 180
      ? [{
          id: branch.id,
          name: branch.name,
          lat: branch.geoLat,
          lng: branch.geoLng,
          index: index + 1,
          menuHref: placePath({ business: business.slug, branch: branch.publicSlug }, {
            experience: 'v2',
            ...(menuViewsByBranch[branch.id] ? { view: menuViewsByBranch[branch.id] } : {}),
          }),
          detailsHref: placeSubPath({ business: business.slug, branch: branch.publicSlug }, 'about', { experience: 'v2' }),
        }]
      : [],
  );
  const selectedMarkerId = selectedId && mapMarkers.some((marker) => marker.id === selectedId) ? selectedId : null;
  const accountHref = placeSubPath({ business: business.slug }, 'account', { experience: 'v2' });

  return (
    <GuestV2Page
      topBar={
        <GuestV2TopBar
          title={business.name}
          subtitle={business.tagline || `${ordered.length} ${ordered.length === 1 ? 'location' : 'locations'}`}
          trailing={<CustomerAccountEntry accountHref={accountHref} chow />}
        />
      }
    >
      <GuestV2Container>
        <header className="pb-8 pt-7 sm:pt-10">
          <p className="gv-eyebrow">{ordered.length} {ordered.length === 1 ? 'location' : 'locations'} nearby</p>
          <h1 className="gv-title mt-2">Choose a location</h1>
          <p className="gv-sub mt-2 max-w-xl">
            Pick where you are visiting or ordering from. Your menu, orders and table requests follow that location.
          </p>
        </header>

        <div className="gv-split pb-12">
          <GuestV2Section
            title="Locations"
            action={
              mapMarkers.length > 0 ? (
                <button
                  type="button"
                  aria-controls="place-hub-location-map"
                  aria-expanded={mobileMapOpen}
                  onClick={() => setMobileMapOpen((open) => !open)}
                  className="gv-btn gv-btn--secondary gv-btn--sm lg:hidden"
                >
                  <MapPin className="size-4" aria-hidden="true" />
                  {mobileMapOpen ? 'Hide map' : 'Map'}
                </button>
              ) : null
            }
          >
            <ul className="gv-group">
              {ordered.map((branch) => {
                const detailsHref = placeSubPath(
                  { business: business.slug, branch: branch.publicSlug },
                  'about',
                  { experience: 'v2' },
                );
                const menuHref = placePath(
                  { business: business.slug, branch: branch.publicSlug },
                  {
                    experience: 'v2',
                    ...(menuViewsByBranch[branch.id] ? { view: menuViewsByBranch[branch.id] } : {}),
                  },
                );
                const meta = [branch.specificType || branch.businessType, branch.address]
                  .filter(Boolean)
                  .join(' · ')
                  .replaceAll('_', ' ');

                return (
                  <li
                    key={branch.id}
                    id={`branch-${branch.id}`}
                    className={cn(
                      'flex items-center gap-3 border-b border-[hsl(var(--chow-rule)/0.75)] p-3.5 last:border-b-0 sm:gap-4 sm:p-4',
                      selectedMarkerId === branch.id && 'bg-[hsl(var(--chow-sunk)/0.55)]',
                    )}
                    onMouseEnter={() => setSelectedId(branch.id)}
                    onFocus={() => setSelectedId(branch.id)}
                  >
                    <Link
                      href={menuHref}
                      aria-label={`Open ${branch.name} menu`}
                      className="flex min-w-0 flex-1 items-center gap-3 rounded-xl outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[hsl(var(--primary))] sm:gap-4"
                    >
                      <span className="gv-photo block h-[84px] w-[104px] sm:h-[96px] sm:w-[128px]">
                        {branch.heroPhoto ? (
                          <ImageWithFallback
                            src={branch.heroPhoto}
                            alt=""
                            className="gv-photo__img"
                            loading="lazy"
                          />
                        ) : (
                          <span className="gv-photo__monogram">
                            {branch.name.trim().slice(0, 1).toUpperCase() || 'M'}
                          </span>
                        )}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-[15px] font-semibold leading-5 text-[hsl(var(--chow-ink))]">
                            {branch.name}
                          </span>
                          {branch.averageRating ? (
                            <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-[hsl(var(--chow-ink))]">
                              <Star className="size-3.5 fill-[hsl(var(--chow-star))] text-[hsl(var(--chow-star))]" aria-hidden="true" />
                              {branch.averageRating.toFixed(1)}
                            </span>
                          ) : null}
                        </span>
                        <span className="mt-1 block truncate text-[13px] leading-5 text-[hsl(var(--chow-soft))]">
                          {branch.openState === 'open' ? (
                            <span className="font-medium text-[hsl(var(--chow-leaf))]">Open now</span>
                          ) : branch.openState === 'closed' ? (
                            <span className="font-medium text-[hsl(var(--chow-destructive))]">Closed</span>
                          ) : (
                            <span>Hours not listed</span>
                          )}
                          {meta ? <span> · {meta}</span> : null}
                        </span>
                      </span>

                      <ArrowRight
                        aria-hidden="true"
                        className="size-4 shrink-0 text-[hsl(var(--chow-soft))] sm:hidden"
                      />
                    </Link>

                    <div className="hidden shrink-0 sm:block">
                      <GuestV2Button href={detailsHref} variant="secondary" size="sm">
                        Details
                        <ArrowRight aria-hidden="true" className="size-4" />
                      </GuestV2Button>
                    </div>
                  </li>
                );
              })}
            </ul>

            {ordered.some((branch) => branch.openState === 'closed') ? (
              <p className="mt-3 px-1 text-xs leading-5 text-[hsl(var(--chow-soft))]">
                Closed locations may still publish their menu. You can browse now and order when they reopen.
              </p>
            ) : null}
          </GuestV2Section>

          {mapMarkers.length > 0 ? (
            <PlaceHubV2Map
              markers={mapMarkers}
              selectedId={selectedMarkerId}
              onSelect={setSelectedId}
              mobileMapOpen={mobileMapOpen}
              unmappedCount={ordered.length - mapMarkers.length}
            />
          ) : null}
        </div>
      </GuestV2Container>
    </GuestV2Page>
  );
}
